import { useState } from "react";
import { toast } from "sonner";
import { User, Mail, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { pesanGalatPembeli } from "../lib/galatPembeli";
import SheetDialog from "./SheetDialog";
import { teleponValid, teleponSimpan, formatTelepon, PESAN_TELEPON } from "../lib/phone";
import { PhoneInput } from "./PhoneInput";

const inputCls = "mt-1.5 w-full rounded-[4px] border border-[#E5E5EA] bg-white px-3.5 py-2.5 text-sm text-[#1D1D1F] outline-none focus:border-[#1D1D1F]";

const Baris = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-[#6E6E73]">{label}</div>
    <div className="mt-0.5 break-words text-sm text-[#1D1D1F]">{children}</div>
  </div>
);

const Aksi = ({ busy, onCancel }) => (
  <div className="mt-5 flex gap-2">
    <button type="submit" disabled={busy} data-testid="profil-simpan"
      className="rounded-[4px] bg-[#1D1D1F] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
      {busy ? "Menyimpan…" : "Simpan"}
    </button>
    <button type="button" onClick={onCancel} disabled={busy} data-testid="profil-batal"
      className="rounded-[4px] border border-[#E5E5EA] px-5 py-2.5 text-sm font-semibold text-[#424245] hover:bg-[#F5F5F7]">
      Batal
    </button>
  </div>
);

function Kartu({ icon: Icon, title, open, onEdit, tid, summary, children }) {
  return (
    <div className="rounded-[8px] border border-[#E5E5EA] p-4" data-testid={tid}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] bg-[#F5F5F7] text-[#1D1D1F]">
          <Icon size={18} strokeWidth={1.6} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="pt-1 font-heading text-base font-bold leading-none text-[#1D1D1F]">{title}</h3>
            {!open && (
              <button type="button" onClick={onEdit} data-testid={`${tid}-ubah`}
                className="shrink-0 text-sm font-semibold text-[#1D1D1F] underline">Ubah</button>
            )}
          </div>
          {!open ? <div className="mt-3 space-y-2">{summary}</div> : <div className="mt-4">{children}</div>}
        </div>
      </div>
    </div>
  );
}

export default function ProfileDialog({ onClose, onBack }) {
  const { user, updateProfile, updateEmail, changePassword } = useAuth();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [telError, setTelError] = useState("");

  const kosongProf = () => ({ nama: user?.nama || "", nama_usaha: user?.nama_usaha || "", telepon: user?.telepon || "" });
  const [prof, setProf] = useState(kosongProf());
  const [em, setEm] = useState({ email: "", current_password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const buka = (name) => {
    if (name === "profile") { setProf(kosongProf()); setTelError(""); }
    if (name === "email") setEm({ email: user?.email || "", current_password: "" });
    if (name === "password") setPw({ current_password: "", new_password: "", confirm_password: "" });
    setEditing(name);
  };

  const simpanProfil = async (e) => {
    e.preventDefault();
    if (!teleponValid(prof.telepon)) { setTelError(PESAN_TELEPON); return; }
    setBusy(true);
    try {
      await updateProfile({ ...prof, telepon: teleponSimpan(prof.telepon) });
      toast.success("Profil disimpan");
      setEditing(null);
    } catch (err) {
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const simpanEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateEmail(em.email, em.current_password);
      toast.success("Profil disimpan");
      setEditing(null);
    } catch (err) {
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const simpanSandi = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("Kata sandi baru minimal 8 karakter"); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("Konfirmasi kata sandi tidak cocok"); return; }
    setBusy(true);
    try {
      await changePassword(pw);
      toast.success("Profil disimpan");
      setEditing(null);
    } catch (err) {
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <SheetDialog title="Profil & kata sandi" onClose={onClose} onBack={onBack} tid="profil-dialog">
      <div className="space-y-4">
        <Kartu icon={User} title="Profil" tid="profil-kartu" open={editing === "profile"} onEdit={() => buka("profile")}
          summary={<>
            <Baris label="Nama lengkap">{user?.nama || "—"}</Baris>
            <Baris label="Nama usaha">{user?.nama_usaha || "—"}</Baris>
            <Baris label="Nomor WhatsApp"><span data-testid="profil-telepon-tampil">{formatTelepon(user?.telepon) || "—"}</span></Baris>
          </>}>
          <form onSubmit={simpanProfil} data-testid="profil-form">
            <label className="block text-sm font-medium">Nama lengkap</label>
            <input required value={prof.nama} onChange={(e) => setProf({ ...prof, nama: e.target.value })} className={inputCls} data-testid="profil-nama" />

            <label className="mt-4 block text-sm font-medium">Nama usaha <span className="text-[#6E6E73]">(opsional)</span></label>
            <input value={prof.nama_usaha} onChange={(e) => setProf({ ...prof, nama_usaha: e.target.value })} className={inputCls} data-testid="profil-usaha" placeholder="cth: Toko Boba Sejahtera" />

            <label className="mt-4 block text-sm font-medium">Nomor WhatsApp <span style={{ color: "#A12622" }}>*</span></label>
            <PhoneInput value={prof.telepon} data-testid="profil-telepon" className={inputCls}
              onChange={(v) => { setProf({ ...prof, telepon: v }); if (telError && teleponValid(v)) setTelError(""); }}
              onBlur={() => setTelError(prof.telepon && !teleponValid(prof.telepon) ? PESAN_TELEPON : "")} />
            {telError && <p className="mt-1 text-xs font-medium" style={{ color: "#A12622" }} data-testid="profil-telepon-error">{telError}</p>}

            <Aksi busy={busy} onCancel={() => setEditing(null)} />
          </form>
        </Kartu>

        <Kartu icon={Mail} title="Email" tid="profil-kartu-email" open={editing === "email"} onEdit={() => buka("email")}
          summary={<Baris label="Alamat email saat ini">{user?.email || "—"}</Baris>}>
          <form onSubmit={simpanEmail} data-testid="profil-email-form">
            <p className="mb-3 text-xs text-[#6E6E73]">Email adalah identitas login dan tujuan seluruh invoice. Masukkan kata sandi saat ini untuk menyimpan.</p>
            <label className="block text-sm font-medium">Email baru</label>
            <input type="email" required value={em.email} onChange={(e) => setEm({ ...em, email: e.target.value })} className={inputCls} data-testid="profil-email" />
            <label className="mt-4 block text-sm font-medium">Kata sandi saat ini</label>
            <input type="password" required value={em.current_password} onChange={(e) => setEm({ ...em, current_password: e.target.value })} className={inputCls} data-testid="profil-email-password" />
            <Aksi busy={busy} onCancel={() => setEditing(null)} />
          </form>
        </Kartu>

        <Kartu icon={Lock} title="Kata sandi" tid="profil-kartu-sandi" open={editing === "password"} onEdit={() => buka("password")}
          summary={<div className="text-sm tracking-widest text-[#1D1D1F]">••••••••</div>}>
          <form onSubmit={simpanSandi} data-testid="profil-sandi-form">
            <label className="block text-sm font-medium">Kata sandi saat ini</label>
            <input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="profil-pw-lama" />
            <label className="mt-4 block text-sm font-medium">Kata sandi baru</label>
            <input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="profil-pw-baru" placeholder="min. 8 karakter" />
            <label className="mt-4 block text-sm font-medium">Konfirmasi kata sandi baru</label>
            <input type="password" required minLength={8} value={pw.confirm_password} onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} className={inputCls} data-testid="profil-pw-konfirmasi" />
            <Aksi busy={busy} onCancel={() => setEditing(null)} />
          </form>
        </Kartu>
      </div>
    </SheetDialog>
  );
}
